<script setup lang="ts">
import { SolButton, SolSelect } from '@solfacil/girassol'
import useAvailableOrders from '~/store-v2/payment-model/useAvailableOrders'
import { getBackofficeStoreOrderUrl, getStoreBaseUrl } from './equipment.constants'

const props = withDefaults(defineProps<{
  projectId: string
  orderId?: string | null
  orderValue?: number | null
  disabled?: boolean
  // Terminal financing: blocks "no order" (store navigation); "view order" stays available
  readOnly?: boolean
  error?: string
  expired?: boolean
}>(), {
  readOnly: false,
})

const emit = defineEmits<{
  (e: 'update:orderId', value: string): void
}>()

const { t, d } = useI18n()

const projectIdRef = computed(() => props.projectId)
const { data: availableOrders, isLoading } = useAvailableOrders(projectIdRef)

function buildOptionLabel(id: string | number, value: number) {
  return t('customer.equipment_step.store_order.option', {
    id,
    value: value.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
  })
}

const orderOptions = computed(() => {
  const options = (availableOrders.value?.data ?? []).map(order => ({
    name: buildOptionLabel(order.order_id, Number(order.value)),
    value: String(order.order_id),
  }))

  const linkedOrderId = props.orderId ? String(props.orderId) : ''
  const isLinkedOrderMissing = linkedOrderId
    && !options.some(option => option.value === linkedOrderId)

  if (isLinkedOrderMissing) {
    options.unshift({
      name: buildOptionLabel(linkedOrderId, props.orderValue ?? 0),
      value: linkedOrderId,
    })
  }

  return options
})

const selectedOrderId = ref<string>(props.orderId ? String(props.orderId) : '')
watch(() => props.orderId, (value) => {
  if (value)
    selectedOrderId.value = String(value)
})

const selectedOption = computed(() =>
  orderOptions.value.find(option => option.value === selectedOrderId.value),
)

const selectedOrder = computed(() =>
  (availableOrders.value?.data ?? []).find(order => String(order.order_id) === selectedOrderId.value),
)

const selectedOrderExpiredDate = computed(() => selectedOrder.value?.expire_at ?? null)

const errorMessage = computed(() => {
  if (props.error)
    return props.error

  if (props.expired && selectedOrderExpiredDate.value)
    return t('customer.equipment_step.store_order.expired_at', { date: d(selectedOrderExpiredDate.value, 'short') })

  return undefined
})

function handleSelectOrder(option?: { name: string, value: string }) {
  selectedOrderId.value = option?.value ?? ''
  emit('update:orderId', selectedOrderId.value)
}

function goToStore() {
  if (props.readOnly)
    return

  window.open(getStoreBaseUrl(), '_blank', 'noopener')
}

function viewOrder() {
  window.open(getBackofficeStoreOrderUrl(selectedOrder.value?.kit?.id), '_blank', 'noopener')
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <span class="fonts-body-medium-bold text-neutral-low-dark">{{ t('customer.equipment_step.store_order.label') }}</span>

    <div class="flex flex-col md:system:flex-row md:system:items-center gap-4">
      <SolSelect
        id="store-order"
        name="store_order"
        class="w-full md:system:flex-1"
        :searchable="true"
        :options="orderOptions"
        :selected="selectedOption"
        :loading="isLoading"
        :disabled="disabled"
        :placeholder="t('customer.equipment_step.store_order.placeholder')"
        :error="errorMessage"
        @update:selected="handleSelectOrder($event as any)"
      />

      <SolButton
        v-if="selectedOrder"
        id="store-order-view"
        variant="secondary"
        size="medium"
        class="w-full md:system:w-auto whitespace-nowrap"
        :disabled="disabled && !readOnly"
        @click="viewOrder"
      >
        {{ t('customer.equipment_step.store_order.view_order') }}
      </SolButton>

      <button
        id="store-order-no-order"
        type="button"
        class="fonts-body-medium-bold text-neutral-low-dark underline whitespace-nowrap"
        :class="{ 'opacity-60 cursor-not-allowed': readOnly }"
        :disabled="readOnly"
        @click="goToStore"
      >
        {{ t('customer.equipment_step.store_order.no_order') }}
      </button>
    </div>

    <span
      v-if="selectedOrderExpiredDate && !expired"
      class="fonts-body-small-regular text-neutral-low-light"
    >
      {{ t('customer.equipment_step.store_order.expires_at', { date: d(selectedOrderExpiredDate, 'short') }) }}
    </span>
  </div>
</template>
